/**
 * 注文作成時にカートアイテムを加工するための関数モジュール
 */
import { log } from '@/log'
import notUndefined from '@/util/collection/not-undefined'
import _ from 'lodash'
import { CartItem } from '../../store/modules/cart/types'

/**
 * カート内にセット商品がある場合、親商品と子商品とで別々の注文とならないように値を調整する。
 * https://github.com/my-color/front/pull/4044
 */
export default function adjustSetItems(cartItems: CartItem[]): CartItem[] {
  const [children, parents, others] = partitionIntoChildrenAndParents(cartItems)

  const adjusted = [
    ...treatChildrenSameAsParents(children, parents),
    ...parents,
    ...others,
  ]

  return restoreOriginalOrder(cartItems, adjusted)
}

function partitionIntoChildrenAndParents(cartItems: CartItem[]): CartItem[][] {
  const [children, parentsOrOthers] = _.partition(cartItems, (i: CartItem) => i.isChild)
  const [parents, others] = _.partition(parentsOrOthers, (i: CartItem) => i.isParent)

  return [children, parents, others]
}

/**
 * カート内商品の並び順を元に戻す。
 */
function restoreOriginalOrder(original: CartItem[], cartItems: CartItem[]): CartItem[] {
  return original.map(o => cartItems.find(i => o.id === i.id)).filter(notUndefined)
}

function treatChildSameAsParent(child: CartItem, parent: CartItem): CartItem {
  return {
    ...child,
    isPreSales: parent.isPreSales, // 親商品が予約商品の場合、その子商品も予約商品として扱う。
    salesStartAt: parent.salesStartAt, // groupPreSalesItemsByWhenSalesStart で親商品と共にグルーピングされるようにするため
  }
}

/**
 * 現状、セット商品の紐付けはRoot単位でしか行われないため、複数のセット商品がカート内にある場合の
 * SKU単位でのセット商品の紐付けは、配列のインデックス順に恣意的に行っている。
 */
function treatChildrenSameAsParents(children: CartItem[], parents: CartItem[]): CartItem[] {
  const excute = (
    _children: CartItem[],
    _parents: CartItem[],
    _updatedChildren: CartItem[] = []
  ): CartItem[] => {
    if (!_children.length) {
      return _updatedChildren
    }

    const [child, ...restOfChildren] = _children
    const parent = _parents.find(maybeParent => (
      maybeParent.childItem ? child.rootId === maybeParent.childItem.id : false
    ))

    if (!parent) {
      log.warn(`The parent item not found for the child item whose id is ${child.rootId} in the cartItems`)

      return excute(
        restOfChildren,
        _parents,
        _updatedChildren
      )
    }

    const restOfParents = _parents.filter(i => i.id !== parent.id)

    return excute(
      restOfChildren,
      restOfParents,
      [..._updatedChildren, treatChildSameAsParent(child, parent)]
    )
  }

  return excute(children, parents)
}
